import {Separator, Tag} from 'platform/components';
import {Box, Clickable, HStack, Show, Text, ThemeColorPath, VStack} from 'platform/foundation';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {RecordAuditResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {RecordAuditScorableResult} from './computeRecordAuditScore';
import {RecordAuditProgressSegments} from './RecordAuditProgressSegments';
import {resolveRecordAuditCounts} from './resolveRecordAuditCounts';

export type RecordAuditScoreFilter = 'all' | 'OK' | 'BAD' | 'NA' | 'notFilled';

interface RecordAuditScoreBarProps {
  audit: RecordAuditResponseBody;
  /**
   * When provided AND audit is OPEN, counts + score are recomputed live from
   * these (overlays applied). Otherwise BE-frozen `audit.statistics` is used.
   */
  liveResults?: ReadonlyArray<RecordAuditScorableResult>;
  activeFilter: RecordAuditScoreFilter;
  onFilterChange: (filter: RecordAuditScoreFilter) => void;
}

export function RecordAuditScoreBar(props: RecordAuditScoreBarProps) {
  const counts = resolveRecordAuditCounts(props.audit, props.liveResults);
  const filled = counts.total - counts.notFilled;
  const ratio = counts.total === 0 ? 0 : filled / counts.total;

  const scoreColor = match(counts.passed)
    .with(true, () => 'success' as const)
    .with(false, () => 'danger' as const)
    .with(Pattern.nullish, () => undefined)
    .exhaustive();

  const threshold = props.audit.definitionSnapshot.successThreshold;

  return (
    <VStack spacing={2}>
      <HStack justify="space-between" align="center" spacing={4} wrap>
        <HStack spacing={2} align="baseline">
          <Text size="xSmall" color="tertiary" alternative>
            {i18n.t('page.recordAudit.detail.completion').toUpperCase()}
          </Text>
          <HStack spacing={1} align="baseline">
            <Text size="large">{`${filled}`}</Text>
            <Text size="large" color="secondary">{`/ ${counts.total}`}</Text>
          </HStack>
          <Text size="xSmall" color="tertiary">
            {`(${Math.round(ratio * 100)}%)`}
          </Text>
        </HStack>
        <HStack spacing={3} align="center" wrap>
          <ScoreLegendItem
            dotColor="palettes.green.60.100"
            label={i18n.t('page.recordAudit.detail.scoreCounts.passed', {count: counts.ok})}
          />
          <ScoreLegendItem
            dotColor="palettes.red.60.100"
            label={i18n.t('page.recordAudit.detail.scoreCounts.failed', {count: counts.bad})}
          />
          <ScoreLegendItem
            dotColor="palettes.neutral.70.100"
            label={i18n.t('page.recordAudit.detail.scoreCounts.na', {count: counts.na})}
          />
          <ScoreLegendItem
            dotColor="palettes.neutral.40.100"
            label={i18n.t('page.recordAudit.detail.scoreCounts.notFilled', {
              count: counts.notFilled,
            })}
          />
        </HStack>
      </HStack>
      <RecordAuditProgressSegments
        ok={counts.ok}
        bad={counts.bad}
        na={counts.na}
        total={counts.total}
      />
      <HStack spacing={2} wrap align="center">
        <ScoreFilterPill
          label={i18n.t('general.labels.all')}
          count={counts.total}
          isActive={props.activeFilter === 'all'}
          onClick={() => props.onFilterChange('all')}
        />
        <ScoreFilterPill
          label={i18n.t('entity.recordAudit.taskVerdict.OK')}
          count={counts.ok}
          dotColor="palettes.green.60.100"
          isActive={props.activeFilter === 'OK'}
          onClick={() => props.onFilterChange('OK')}
        />
        <ScoreFilterPill
          label={i18n.t('entity.recordAudit.taskVerdict.BAD')}
          count={counts.bad}
          dotColor="palettes.red.60.100"
          isActive={props.activeFilter === 'BAD'}
          onClick={() => props.onFilterChange('BAD')}
        />
        <ScoreFilterPill
          label={i18n.t('entity.recordAudit.taskVerdict.NA')}
          count={counts.na}
          dotColor="palettes.neutral.70.100"
          isActive={props.activeFilter === 'NA'}
          onClick={() => props.onFilterChange('NA')}
        />
        <ScoreFilterPill
          label={i18n.t('entity.recordAudit.score.notFilled')}
          count={counts.notFilled}
          dotColor="palettes.neutral.40.100"
          isActive={props.activeFilter === 'notFilled'}
          onClick={() => props.onFilterChange('notFilled')}
        />
      </HStack>
      {/* Weighted score verdict vs the definition's success threshold — distinct from the
          completion ratio above, which only tracks how many tasks are filled. */}
      <Show when={isNotNil(counts.percentage)}>
        <Separator spacing={0} />
        <HStack justify="space-between" align="center" spacing={2} wrap>
          <HStack spacing={2} align="baseline" wrap>
            <Text size="xSmall" color="tertiary" alternative>
              {i18n.t('page.recordAudit.detail.score').toUpperCase()}
            </Text>
            <Text size="large" color={scoreColor}>
              {`${counts.percentage}%`}
            </Text>
            <Text size="xSmall" color="tertiary">
              {i18n.t('page.recordAudit.create.wizard.passThreshold', {threshold})}
            </Text>
          </HStack>
          <Show when={isNotNil(counts.passed)}>
            <Tag
              label={(counts.passed
                ? i18n.t('page.recordAudit.detail.passed')
                : i18n.t('page.recordAudit.detail.failed')
              ).toUpperCase()}
              colorScheme={counts.passed ? 'green' : 'red'}
            />
          </Show>
        </HStack>
      </Show>
    </VStack>
  );
}

// Shared dot size (8px on the 4px step grid) so the legend and the filter pills
// below read as one set.
const DOT_SIZE = 2;

interface ScoreLegendItemProps {
  label: string;
  dotColor: ThemeColorPath;
}

// Read-only summary count beside the completion header (passed / failed / N/A / empty).
function ScoreLegendItem(props: ScoreLegendItemProps) {
  return (
    <HStack spacing={2} align="center">
      <Box
        width={DOT_SIZE}
        height={DOT_SIZE}
        borderRadius="circular"
        backgroundColor={props.dotColor}
      />
      <Text size="xSmall" color="tertiary" noWrap>
        {props.label}
      </Text>
    </HStack>
  );
}

interface ScoreFilterPillProps {
  label: string;
  count: number;
  dotColor?: ThemeColorPath;
  isActive?: boolean;
  onClick: () => void;
}

// Clickable filter pill matching the mockup: optional colour dot, label, and a
// count badge. The active filter renders as the dark pill.
function ScoreFilterPill(props: ScoreFilterPillProps) {
  return (
    <Clickable onClick={props.onClick}>
      <Box
        borderRadius="circular"
        border="1px solid"
        borderColor={props.isActive ? 'palettes.neutral.900.100' : 'palettes.neutral.40.100'}
        backgroundColor={props.isActive ? 'palettes.neutral.900.100' : 'general.white'}
        paddingHorizontal={2}
        paddingVertical={1}
      >
        <HStack spacing={2} align="center">
          <Show when={isNotNil(props.dotColor)}>
            <Box
              width={DOT_SIZE}
              height={DOT_SIZE}
              borderRadius="circular"
              backgroundColor={props.dotColor}
            />
          </Show>
          <Text size="xSmall" alternative color={props.isActive ? 'white' : 'primary'} noWrap>
            {props.label}
          </Text>
          <Box
            borderRadius="circular"
            backgroundColor={
              props.isActive ? 'palettes.neutral.700.100' : 'palettes.neutral.20.100'
            }
            paddingHorizontal={2}
          >
            <Text size="xSmall" color={props.isActive ? 'white' : 'tertiary'}>
              {props.count}
            </Text>
          </Box>
        </HStack>
      </Box>
    </Clickable>
  );
}
